import firebase from "./firebase";
import "firebase/compat/auth";
import { axiosGraphQL as defaultAxiosGraphQL } from "../api";
import { CONFIRM_EMAIL_CHANGE, GET_USER, SEND_EMAIL_CHANGE_LINK } from "../graphql/userGraphql";
import { getStorage } from "./utils";
import { persistFirebaseToken, storeFirebaseToken } from "./firebaseSession";
import {
  beginEmailLinkRequest,
  clearPendingEmailChange,
  EMAIL_CHANGE_MODES,
  getPendingEmailChange,
  normalizeEmail,
  ownsEmailLinkRequest,
  PENDING_EMAIL_CHANGE_KEY,
  releaseEmailLinkRequest,
  savePendingEmailChange,
} from "./pendingEmailChange";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isLocalEmailLink = () =>
  process.env.REACT_APP_NODE_ENV === "development" &&
  window.location.hostname.includes("localhost");
export const isExplicitlyImpersonating = () => Boolean(getStorage().getItem("impersonate"));

export const initiateEmailChange = async ({ email, language, axiosGraphQL = defaultAxiosGraphQL }) => {
  if (isExplicitlyImpersonating()) throw new Error("email_change_impersonation_forbidden");
  const currentUser = firebase.auth().currentUser;
  if (!currentUser?.uid) throw new Error("email_change_auth_required");
  const normalizedEmail = normalizeEmail(email);
  if (!EMAIL_PATTERN.test(normalizedEmail)) throw new Error("invalid_email");

  const requestId = beginEmailLinkRequest();
  try {
    await persistFirebaseToken(currentUser);
  } catch (_) {
    releaseEmailLinkRequest(requestId);
    throw new Error("email_change_auth_required");
  }
  if (!ownsEmailLinkRequest(requestId)) {
    throw new Error("email_link_request_superseded");
  }

  const mode = currentUser.email
    ? EMAIL_CHANGE_MODES.VERIFY_AND_CHANGE
    : EMAIL_CHANGE_MODES.LINK;
  try {
    localStorage.removeItem("emailForSignIn");
    savePendingEmailChange({ email: normalizedEmail, firebaseUid: currentUser.uid, mode });
    const response = await axiosGraphQL({
      query: SEND_EMAIL_CHANGE_LINK,
      variables: { email: normalizedEmail, language, isLocal: isLocalEmailLink() },
    });
    if (response.data?.errors?.length) throw new Error(response.data.errors[0]?.message || "email_change_failed");
    releaseEmailLinkRequest(requestId);
    return { email: normalizedEmail, mode };
  } catch (error) {
    if (ownsEmailLinkRequest(requestId)) {
      clearPendingEmailChange();
      releaseEmailLinkRequest(requestId);
    }
    throw error;
  }
};

export { persistFirebaseToken } from "./firebaseSession";

const responseUser = (response) => {
  const me = response.data?.data?.me;
  if (!me?.user) throw new Error("email_change_user_refresh_failed");
  return { me, user: me.user };
};

export const refreshAuthoritativeUser = async ({ axiosGraphQL, previousUser }) => {
  const { me, user } = responseUser(await axiosGraphQL({ query: GET_USER }));
  user.clubs = me.clubs;
  const activeClubId = getStorage().getItem("activeClubId") || previousUser?.club?._id;
  user.club = me.clubs?.find((club) => club?._id === activeClubId) || me.club;
  return user;
};

export const isTerminalEmailChangeError = (error) => {
  const value = `${error?.code || ""} ${error?.message || ""}`.toLowerCase();
  return ["expired", "invalid-action-code", "invalid-credential", "credential-already-in-use", "email-already-in-use", "uid_mismatch", "account_mismatch"].some((part) => value.includes(part));
};

export const completeEmailChangeAction = async ({ mode, oobCode, actionUrl, axiosGraphQL = defaultAxiosGraphQL, previousUser }) => {
  if (isExplicitlyImpersonating()) throw new Error("email_change_impersonation_forbidden");
  const currentUser = firebase.auth().currentUser;
  if (!currentUser?.uid) {
    clearPendingEmailChange();
    throw new Error("email_change_auth_required");
  }
  const isExistingEmailChange = mode === "verifyAndChangeEmail";
  const pendingMode = isExistingEmailChange ? EMAIL_CHANGE_MODES.VERIFY_AND_CHANGE : EMAIL_CHANGE_MODES.LINK;
  const pending = getPendingEmailChange({ firebaseUid: currentUser.uid, mode: pendingMode });
  const pendingSnapshot = localStorage.getItem(PENDING_EMAIL_CHANGE_KEY);
  const clearCompletedPendingAction = () => {
    if (localStorage.getItem(PENDING_EMAIL_CHANGE_KEY) === pendingSnapshot) clearPendingEmailChange();
  };
  // Existing-address changes are authoritatively bound to the authenticated
  // Core user, Firebase UID, target email, action code, and expiry server-side.
  // Browser storage is useful for continuity, but must not be an additional
  // requirement when the secure server binding is still valid.
  if (!pending && !isExistingEmailChange) return null;
  let expectedEmail = pending?.email;

  try {
    if (isExistingEmailChange) {
      if (!oobCode) throw new Error("invalid-action-code");
      // Callback routing defers ordinary session bootstrap. Bind confirmation to
      // the restored Firebase account, never a previous account's cached bearer.
      const sessionToken = await currentUser.getIdToken(true);
      if (firebase.auth().currentUser?.uid !== currentUser.uid) throw new Error("account_mismatch");
      storeFirebaseToken(currentUser, sessionToken);
      const confirmation = await axiosGraphQL(
        { query: CONFIRM_EMAIL_CHANGE, variables: { oobCode } },
        { sessionToken, expectedFirebaseUid: currentUser.uid, withoutClub: true }
      );
      if (firebase.auth().currentUser?.uid !== currentUser.uid) throw new Error("account_mismatch");
      if (confirmation.data?.errors?.length) throw new Error(confirmation.data.errors[0]?.message || "email_change_failed");
      const customToken = confirmation.data?.data?.confirmEmailChange?.customToken;
      if (customToken) {
        const credential = await firebase.auth().signInWithCustomToken(customToken);
        const refreshedFirebaseUser = credential.user || firebase.auth().currentUser;
        expectedEmail ||= normalizeEmail(refreshedFirebaseUser?.email);
        await persistFirebaseToken(refreshedFirebaseUser);
      } else {
        if (!pending) throw new Error("email_change_custom_token_required");
        try {
          await persistFirebaseToken(firebase.auth().currentUser);
        } catch (_) {
          throw new Error("email_change_custom_token_required");
        }
      }
    } else {
      let linkedUser = currentUser;
      const emailAlreadyLinked = normalizeEmail(currentUser.email) === pending.email;
      if (!emailAlreadyLinked) {
        const credential = firebase.auth.EmailAuthProvider.credentialWithLink(pending.email, actionUrl);
        const linked = await currentUser.linkWithCredential(credential);
        linkedUser = linked.user || currentUser;
      }
      await persistFirebaseToken(linkedUser);
      const confirmation = await axiosGraphQL({ query: CONFIRM_EMAIL_CHANGE, variables: {} });
      if (confirmation.data?.errors?.length) throw new Error(confirmation.data.errors[0]?.message || "email_change_failed");
    }
    const user = await refreshAuthoritativeUser({ axiosGraphQL, previousUser });
    if (!expectedEmail || normalizeEmail(user.email) !== expectedEmail) {
      throw new Error("email_change_sync_pending");
    }
    clearCompletedPendingAction();
    return { user, token: getStorage().getItem("token") };
  } catch (error) {
    if (isTerminalEmailChangeError(error)) clearCompletedPendingAction();
    throw error;
  }
};
